/**
 * Broker Vuex Store Module
 * Manages broker-specific state and authentication
 */

import { Module } from 'vuex'
import authRepository, { BrokerSession, isMfaChallenge } from '../repositories/auth.repository'
import partnerService, { BrokerInfo } from '../services/partner.service'
import { getResponsiveLogo } from '@/utils/partnerLogos'
import { Permission, StaffRole, hasPermission, hasAnyPermission, getPermissions, ROLE_LABELS } from '../types/roles'

export interface BrokerUser {
  id: number // Real numeric user ID from /api/account
  username: string // Login username
  email: string
  firstName: string
  lastName: string
  imageUrl?: string
  roles: string[]
  partnerId: number
  mainClubId?: number | null
  partnerStaffRole?: string 
}

export interface BrokerState {
  // Authentication
  isAuthenticated: boolean
  token: string | null
  partnerId: number | null
  user: BrokerUser | null

  // Broker/Partner Info
  brokerInfo: BrokerInfo | null

  // UI State
  loading: boolean
  error: string | null
}

/**
 * Shared tail of both login steps: seeds the store from the JWT, then tries to
 * enrich the user from /api/account without failing the login if that call breaks.
 */
async function establishSession(commit: any, dispatch: any, session: BrokerSession, email: string): Promise<void> {
  // Note: id will be set to the real user ID when fetchUserAccount is called
  const user: BrokerUser = {
    id: 0, // Temporary - will be replaced with real ID from /api/account
    username: session.user.username,
    email,
    firstName: '',
    lastName: '',
    roles: session.user.roles,
    partnerId: session.partnerId,
    partnerStaffRole: session.user.partnerStaffRole
  }

  commit('SET_AUTH', {
    token: session.token,
    partnerId: session.partnerId,
    user
  })

  // Fetch full user account details including profile picture
  try {
    await dispatch('fetchUserAccount')
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch user account details after login:', error?.message || error)
    }
    // Don't fail login if user account fetch fails
  }
}

const brokerModule: Module<BrokerState, any> = {
  namespaced: true,

  state: {
    isAuthenticated: false,
    token: null,
    partnerId: null,
    user: null,
    brokerInfo: null,
    loading: false,
    error: null
  },

  mutations: {
    SET_AUTH(state, { token, partnerId, user }) {
      state.isAuthenticated = true
      state.token = token
      state.partnerId = partnerId
      state.user = user
    },

    CLEAR_AUTH(state) {
      state.isAuthenticated = false
      state.token = null
      state.partnerId = null
      state.user = null
      state.brokerInfo = null
    },

    SET_BROKER_INFO(state, brokerInfo: BrokerInfo | null) {
      state.brokerInfo = brokerInfo
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },

    SET_ERROR(state, error: string | null) {
      state.error = error
    },

    SET_USER(state, user: BrokerUser) {
      state.user = user
    },

    SET_MAIN_CLUB_ID(state, clubId: number | null) {
      if (state.user) {
        state.user.mainClubId = clubId
      }
    }
  },

  actions: {
    async login({ commit, dispatch }, { username, password }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      try {
        const response = await authRepository.login(username, password)

        // SFB-1164: the password was right but the account owes a second factor.
        // Nothing is stored yet; the login UI switches to the code step and calls
        // `verifyTwoFactor` with this challenge.
        if (isMfaChallenge(response)) {
          return {
            success: false,
            mfaRequired: true,
            mfaToken: response.mfaToken,
            expiresIn: response.expiresIn
          }
        }

        await establishSession(commit, dispatch, response, username)
        return { success: true }
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Login error in store:', error?.message || error)
        }
        const errorMessage = error.response?.data?.message || error.message || 'Invalid credentials'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Second login step (SFB-1164). Mirrors `login` once the backend hands back
     * the session token. Rejections keep the axios error under `cause` so the
     * UI can map the `error.2fa.*` key to a message.
     */
    async verifyTwoFactor({ commit, dispatch }, { mfaToken, code, username }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const session = await authRepository.verifyTwoFactor(mfaToken, code)
        await establishSession(commit, dispatch, session, username)
        return { success: true }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Verification failed'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage, cause: error }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit }) {
      await authRepository.logout()
      commit('CLEAR_AUTH')
    },

    async checkAuth({ commit, dispatch }) {
      if (authRepository.isAuthenticated()) {
        const token = authRepository.getToken()
        const partnerId = authRepository.getPartnerId()
        const username = localStorage.getItem('broker_user')

        if (token && partnerId && username) {
          // Reconstruct initial user from stored data
          // Note: id will be set to the real user ID when fetchUserAccount is called
          const user: BrokerUser = {
            id: 0, // Temporary - will be replaced with real ID from /api/account
            username,
            email: '',
            firstName: '',
            lastName: '',
            roles: [],
            partnerId
          }

          commit('SET_AUTH', { token, partnerId, user })

          // Fetch full user account details including profile picture
          try {
            await dispatch('fetchUserAccount')
          } catch (error: any) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.error('Failed to fetch user account details on auth check:', error?.message || error)
            }
            // Don't fail auth check if user account fetch fails
          }

          return true
        }
      }

      commit('CLEAR_AUTH')
      return false
    },

    async setMainClubId({ commit, state }, clubId: number | null) {
      if (state.user) {
        commit('SET_MAIN_CLUB_ID', clubId)
      }
    },

    async fetchUserAccount({ commit, state }) {
      try {
        const userAccount = await authRepository.getUser()

        // Update user with account details including the REAL numeric user ID
        if (state.user) {
          const updatedUser: BrokerUser = {
            ...state.user,
            id: userAccount.id, // This is the REAL numeric user ID
            username: userAccount.login, // Keep the login/username
            email: userAccount.email,
            firstName: userAccount.firstName,
            lastName: userAccount.lastName,
            imageUrl: userAccount.imageUrl,
            roles: userAccount.authorities,
            partnerStaffRole: userAccount.partnerStaffRole
          }

          commit('SET_USER', updatedUser)
        }

        return userAccount
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch user account:', error?.message || error)
        }
        throw error
      }
    },

    async fetchBrokerInfo({ commit }) {
      try {
        const brokerInfo = await partnerService.getBrokerInfo()
        commit('SET_BROKER_INFO', brokerInfo)
        return brokerInfo
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch broker info:', error?.message || error)
        }
        commit('SET_BROKER_INFO', null)
        return null
      }
    }
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    currentUser: (state) => state.user,
    mainClubId: (state) => state.user ? state.user.mainClubId : null,
    partnerId: (state) => state.partnerId,
    authToken: (state) => state.token,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    brokerInfo: (state) => state.brokerInfo,
    brokerLogoUrl: (state) => {
      if (!state.brokerInfo?.partner) return null
      return getResponsiveLogo(state.brokerInfo.partner) || null
    },
    brokerName: (state) => {
      if (!state.brokerInfo) return ''
      return state.brokerInfo.partner?.shortName || state.brokerInfo.partner?.fullName || state.brokerInfo.name || ''
    }, 
    // RBAC getters
    staffRole: (state) => state.user?.partnerStaffRole || null,
    staffRoleLabel: (state) => ROLE_LABELS[state.user?.partnerStaffRole || ''] || state.user?.partnerStaffRole || '',
    staffPermissions: (state) => getPermissions(state.user?.partnerStaffRole),
    hasPermission: (state) => (permission: Permission) =>
      hasPermission(state.user?.partnerStaffRole, permission),
    hasAnyPermission: (state) => (permissions: Permission[]) =>
      hasAnyPermission(state.user?.partnerStaffRole, permissions),

    // Convenience getters for navigation visibility
    canViewCompetitions: (state) =>
      hasAnyPermission(state.user?.partnerStaffRole, [Permission.COMPETITION_LIST_ALL, Permission.COMPETITION_LIST_MY]),
    canViewIBs: (state) =>
      hasAnyPermission(state.user?.partnerStaffRole, [Permission.IB_LIST_ALL, Permission.IB_LIST_MY]),
    canViewStaff: (state) =>
      hasPermission(state.user?.partnerStaffRole, Permission.STAFF_LIST_ALL),
    canViewPlatformGroups: (state) =>
      hasPermission(state.user?.partnerStaffRole, Permission.PLATFORM_GROUP_LIST_ALL),
    // Email templates are scoped to tournaments — gated on the same permission the
    // backend enforces (COMPETITION_CREATE on BrokerAdminTournamentResource).
    canManageEmailTemplates: (state) =>
      hasPermission(state.user?.partnerStaffRole, Permission.COMPETITION_CREATE),
    // Public API docs (SFB-1237) describe partner-wide, api-key integrations, so
    // only staff with partner-wide access see the entry point: legacy ADMIN plus
    // MANAGER/OPERATIONS. There is no backend permission for this yet — the
    // docs themselves are public — hence a role list instead of a Permission.
    canViewApiDocs: (state) =>
      [StaffRole.ADMIN, StaffRole.MANAGER, StaffRole.OPERATIONS].includes(state.user?.partnerStaffRole as StaffRole),
  }
}

export default brokerModule